import type { ReactElement } from 'react';
import {
  type BoxNode,
  type FlatView,
  rotationFeedback,
  type ViewportSize,
} from '@/features/design-editor';

interface RotationFeedbackProps {
  readonly node: BoxNode;
  readonly view: FlatView;
  /** 캔버스 화면 크기 — 각도 표시가 밖으로 나가면 반대편으로 뒤집는다. */
  readonly viewport: ViewportSize;
}

/**
 * 회전 핸들을 끄는 동안의 각도 표시와, 직각(0·90·180·270°)에 붙었을 때 노드 가운데를 지나는 수평·수직 안내선.
 * 놓으면 캔버스가 그리지 않는다.
 */
export const RotationFeedback = ({ node, view, viewport }: RotationFeedbackProps): ReactElement => {
  const { center, badge, label, guide } = rotationFeedback(node, view, viewport);
  return (
    <>
      {guide !== null && (
        <svg className="pointer-events-none absolute inset-0 size-full" aria-hidden>
          <line
            data-testid="rotation-guide"
            data-axis={guide}
            x1={guide === 'horizontal' ? '0' : center.x}
            x2={guide === 'horizontal' ? '100%' : center.x}
            y1={guide === 'vertical' ? '0' : center.y}
            y2={guide === 'vertical' ? '100%' : center.y}
            className="stroke-primary"
            strokeWidth={1}
            strokeDasharray="4 3"
          />
        </svg>
      )}
      <p
        data-testid="rotation-badge"
        className="pointer-events-none absolute flex h-5 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-md bg-foreground/85 text-[11px] font-medium text-background tabular-nums shadow-sm"
        style={{ left: badge.x, top: badge.y }}
      >
        {label}
      </p>
    </>
  );
};
